<template>
  <v-card class="px-4 pb-4 pt-2 otx-feed-card" flat>
    <div class="d-flex align-start justify-space-between mb-2 header-row">
      <div class="feed-title-wrap pr-3">
        <span class="section-title">Threat Intelligence Feed</span>
      </div>

      <div class="d-flex align-center otx-toolbar-controls">
        <v-menu
          v-model="sortMenu"
          offset-y
          left
          :close-on-content-click="false"
          content-class="otx-control-menu"
          max-width="340"
          min-width="300"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tipOn, attrs: tipAttrs }">
                <v-btn
                  icon
                  small
                  class="mr-1 otx-icon-btn"
                  v-bind="{ ...attrs, ...tipAttrs }"
                  v-on="{ ...on, ...tipOn }"
                >
                  <v-icon small :color="inactiveIconColor">mdi-sort</v-icon>
                </v-btn>
              </template>
              <span>Sort</span>
            </v-tooltip>
          </template>

          <v-card flat>
            <v-card-title class="text-subtitle-2 font-weight-medium pb-2">
              Sort
            </v-card-title>

            <v-card-text class="pb-4">
              <div class="d-flex align-center sort-select-row">
                <v-select
                  v-model="sortBy"
                  :items="sortOptions"
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  label="Sort by"
                  class="flex-grow-1 mr-2"
                  @change="applySortChange"
                />

                <v-btn
                  icon
                  small
                  class="sort-direction-btn"
                  @click="toggleSortOrder"
                >
                  <v-icon :color="inactiveIconColor">
                    {{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                  </v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-menu>

        <v-menu
          v-model="filterMenu"
          offset-y
          left
          :close-on-content-click="false"
          content-class="otx-control-menu"
          max-width="340"
          min-width="300"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tipOn, attrs: tipAttrs }">
                <v-btn
                  icon
                  small
                  class="mr-1 otx-icon-btn"
                  v-bind="{ ...attrs, ...tipAttrs }"
                  v-on="{ ...on, ...tipOn }"
                >
                  <v-icon small :color="hasActiveFilters ? activeIconColor : inactiveIconColor">
                    mdi-filter-variant
                  </v-icon>
                </v-btn>
              </template>
              <span>Filters</span>
            </v-tooltip>
          </template>

          <v-card flat>
            <v-card-title class="text-subtitle-2 font-weight-medium pb-2">
              Filters
            </v-card-title>

            <v-card-text class="pb-2">
              <v-text-field
                v-model="draftFilters.softwareTitle"
                dense
                outlined
                clearable
                hide-details
                label="Software title"
                hint="Example: exchange, fortios, apache struts"
                persistent-hint
                class="mb-3"
              />

              <v-text-field
                v-model="draftFilters.author"
                dense
                outlined
                clearable
                hide-details
                label="Author"
                class="mb-3"
              />

              <v-select
                v-model="draftFilters.tlps"
                :items="tlpOptions"
                dense
                outlined
                hide-details
                label="TLP"
                multiple
                chips
                small-chips
                clearable
                class="mb-3"
              />

              <div class="d-flex align-center">
                <v-text-field
                  v-model="draftFilters.minIndicators"
                  type="number"
                  dense
                  outlined
                  hide-details
                  label="Min indicators"
                  class="mr-2"
                />
                <v-text-field
                  v-model="draftFilters.maxIndicators"
                  type="number"
                  dense
                  outlined
                  hide-details
                  label="Max indicators"
                />
              </div>
            </v-card-text>

            <v-card-actions class="px-4 pb-4 pt-0">
              <v-btn
                outlined
                color="primary"
                class="otx-action-btn mr-2"
                @click="resetFilterSettings"
              >
                Reset
              </v-btn>

              <v-spacer />

              <v-btn
                outlined
                color="primary"
                class="otx-action-btn"
                @click="applyFilterSettings"
              >
                Apply
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>

        <v-menu
          v-model="settingsMenu"
          offset-y
          left
          :close-on-content-click="false"
          content-class="otx-control-menu"
          max-width="520"
          min-width="420"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tipOn, attrs: tipAttrs }">
                <v-btn
                  icon
                  small
                  class="otx-icon-btn"
                  v-bind="{ ...attrs, ...tipAttrs }"
                  v-on="{ ...on, ...tipOn }"
                >
                  <v-icon small :color="inactiveIconColor">mdi-cog-outline</v-icon>
                </v-btn>
              </template>
              <span>Settings</span>
            </v-tooltip>
          </template>

          <v-card flat>
            <v-card-title class="text-subtitle-2 font-weight-medium pb-2">
              Data Settings
            </v-card-title>

            <v-card-text class="pb-2">
              <v-select
                v-model="draftDateSettings.dateField"
                :items="dateFieldOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                hide-details
                label="Date type"
                class="mb-3"
              />

              <DateRange
                :start_date="draftDateSettings.dateFrom"
                :end_date="draftDateSettings.dateTo"
                :min_start_date="minStartDate"
                :max_end_date="maxEndDate"
                @date_update="onDateRangeUpdate"
              />
            </v-card-text>

            <v-card-actions class="px-4 pb-4 pt-0">
              <v-spacer />
              <v-btn outlined color="primary" class="otx-action-btn" @click="applyDateSettings">
                Apply
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </div>
    </div>

    <div class="d-flex align-center justify-space-between mb-3 meta-row">
      <div class="caption grey--text text--darken-1 active-filter-summary">
        {{ summaryBlurb }}
      </div>
      <span class="caption grey--text text--darken-1 text-right otx-updated-text">
        {{ lastUpdated ? `Updated ${lastUpdated}` : '' }}
      </span>
    </div>

    <div class="otx-content-area">
      <v-alert v-if="error && !items.length && !loading" type="error" dense outlined>
        {{ error }}
      </v-alert>

      <div v-else-if="!items.length && !loading" class="grey--text text--darken-1 empty-state">
        No OTX feed items match your current settings.
      </div>

      <div v-else class="otx-scroll-container">
        <v-list dense class="py-0 otx-list-fill">
          <v-list-item v-for="item in items" :key="item.id" class="px-0 otx-list-item">
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium otx-title">
                <a
                  :href="getPulseUrl(item)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="otx-title-link"
                >
                  {{ item.name || 'Untitled pulse' }}
                </a>
              </v-list-item-title>

              <v-list-item-subtitle class="otx-subtitle">
                {{ item.author || 'Unknown author' }}
                <span class="mx-1">·</span>
                {{ normalizeTlp(item.tlp) }}
                <span class="mx-1">·</span>
                {{ formatDate(getItemDateValue(item, dateSettings.dateField)) }}
                <span v-if="typeof getIndicatorCount(item) === 'number'">
                  <span class="mx-1">·</span>
                  {{ getIndicatorCount(item) }} indicators
                </span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </div>

      <div v-if="loading" class="otx-loading-overlay">
        <v-skeleton-loader
          class="otx-skeleton-fill"
          type="list-item-three-line,list-item-three-line,list-item-three-line,list-item-three-line,list-item-three-line,list-item-three-line"
        />
      </div>
    </div>

    <div v-if="error && items.length" class="caption error--text mt-2">
      {{ error }}
    </div>
  </v-card>
</template>

<script>
import apiCaller from "@/javascript/chart_config_retrieval.js";
import DateRange from "../UtilityComponents/DateRange.vue";

const DEFAULT_SORT_BY = "modified";
const DEFAULT_SORT_ORDER = "desc";
const CACHE_TTL_MS = 45000;

const ACTIVE_ICON_COLOR = "#020446";
const INACTIVE_ICON_COLOR = "#2B50FF";

const DEFAULT_FILTERS = () => ({
  softwareTitle: "",
  author: "",
  tlps: [],
  minIndicators: "",
  maxIndicators: "",
});

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 7);

  return {
    dateField: "modified",
    dateFrom: start.toISOString().substr(0, 10),
    dateTo: end.toISOString().substr(0, 10),
  };
};

export default {
  name: "HomeOTXFeed",

  components: {
    DateRange,
  },

  props: {
    refreshMs: {
      type: Number,
      default: 60000,
    },
  },

  data() {
    return {
      loading: false,
      error: "",
      items: [],
      pollHandle: null,
      lastUpdated: "",
      requestInFlight: false,
      responseCache: {},

      sortMenu: false,
      filterMenu: false,
      settingsMenu: false,

      sortBy: DEFAULT_SORT_BY,
      sortOrder: DEFAULT_SORT_ORDER,
      filters: DEFAULT_FILTERS(),
      dateSettings: getDefaultDateRange(),

      draftFilters: DEFAULT_FILTERS(),
      draftDateSettings: getDefaultDateRange(),

      sortOptions: [
        { text: "Modified", value: "modified" },
        { text: "Created", value: "created" },
        { text: "Author", value: "author" },
        { text: "Name", value: "name" },
        { text: "TLP", value: "tlp" },
        { text: "Indicators", value: "indicator_count" },
      ],

      dateFieldOptions: [
        { text: "Modified", value: "modified" },
        { text: "Created", value: "created" },
      ],

      tlpOptions: ["red", "amber", "green", "white"],
      activeIconColor: ACTIVE_ICON_COLOR,
      inactiveIconColor: INACTIVE_ICON_COLOR,
    };
  },

  computed: {
    summaryBlurb() {
      const rangeText = this.getSelectedRangeSummary();
      const itemCount = Array.isArray(this.items) ? this.items.length : 0;

      return `${rangeText} · ${itemCount} item${itemCount === 1 ? "" : "s"} shown`;
    },

    hasActiveFilters() {
      return Boolean(
        (this.filters.softwareTitle || "").trim() ||
          (this.filters.author || "").trim() ||
          (Array.isArray(this.filters.tlps) && this.filters.tlps.length) ||
          this.isFiniteNumber(this.filters.minIndicators) ||
          this.isFiniteNumber(this.filters.maxIndicators)
      );
    },

    minStartDate() {
      const now = new Date();
      now.setFullYear(now.getFullYear() - 2);
      return now.toISOString().substr(0, 10);
    },

    maxEndDate() {
      return new Date().toISOString().substr(0, 10);
    },
  },

  mounted() {
    this.loadFeed();

    this.pollHandle = window.setInterval(() => {
      this.loadFeed(true);
    }, this.refreshMs);
  },

  beforeDestroy() {
    if (this.pollHandle) {
      window.clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  },

  methods: {
    async loadFeed(silent = false) {
      const requestPayload = this.buildRequestPayload();
      const cacheKey = this.getCacheKey(requestPayload);
      const cachedEntry = this.responseCache[cacheKey];
      const now = Date.now();

      if (cachedEntry && now - cachedEntry.timestamp < CACHE_TTL_MS) {
        this.items = this.sortItems([...cachedEntry.items], this.sortBy, this.sortOrder);
        this.error = "";
        this.lastUpdated = new Date().toLocaleTimeString();
        return;
      }

      if (this.requestInFlight) {
        return;
      }

      this.requestInFlight = true;
      if (!silent) {
        this.loading = true;
      }
      this.error = "";

      const loadStart = Date.now();

      try {
        const token = await this.$auth.getTokenSilently();
        const resp = await apiCaller.get_otx_feed(token, requestPayload);

        if (resp && resp.success) {
          const responseItems = Array.isArray(resp.data) ? resp.data : [];
          this.responseCache[cacheKey] = {
            timestamp: Date.now(),
            items: [...responseItems],
          };
          this.items = this.sortItems([...responseItems], this.sortBy, this.sortOrder);
          this.lastUpdated = new Date().toLocaleTimeString();
        } else {
          this.error = resp && resp.message ? resp.message : "Failed to load OTX feed";
        }
      } catch (e) {
        console.log("HomeOTXFeed load error:", e);
        this.error =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          "Failed to load OTX feed";
      } finally {
        const elapsed = Date.now() - loadStart;
        const remaining = Math.max(0, 250 - elapsed);
        window.setTimeout(() => {
          this.loading = false;
          this.requestInFlight = false;
        }, remaining);
      }
    },

    buildRequestPayload() {
      const payload = {
        date_field: this.dateSettings.dateField,
      };

      const softwareTitles = this.parseSoftwareTitles(this.filters.softwareTitle);
      if (softwareTitles.length) {
        payload.software_titles = softwareTitles;
      }

      if (this.filters.author) {
        payload.author_name = this.filters.author.trim();
      }

      if (Array.isArray(this.filters.tlps) && this.filters.tlps.length) {
        payload.tlp = [...this.filters.tlps];
      }

      if (this.isFiniteNumber(this.filters.minIndicators)) {
        payload.min_indicators = Number(this.filters.minIndicators);
      }

      if (this.isFiniteNumber(this.filters.maxIndicators)) {
        payload.max_indicators = Number(this.filters.maxIndicators);
      }

      if (this.dateSettings.dateFrom) {
        payload.date_from = this.toIsoStartOfDay(this.dateSettings.dateFrom);
      }

      if (this.dateSettings.dateTo) {
        payload.date_to = this.toIsoEndOfDay(this.dateSettings.dateTo);
      }

      if (this.dateSettings.dateField === "modified" && this.dateSettings.dateFrom) {
        payload.modified_since = this.toIsoStartOfDay(this.dateSettings.dateFrom);
      }

      return payload;
    },

    parseSoftwareTitles(value) {
      if (!value) {
        return [];
      }

      return String(value)
        .split(",")
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean);
    },

    getCacheKey(payload) {
      return JSON.stringify(payload);
    },

    applySortChange() {
      this.items = this.sortItems([...this.items], this.sortBy, this.sortOrder);
    },

    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      this.applySortChange();
    },

    applyFilterSettings() {
      this.filters = {
        softwareTitle: this.draftFilters.softwareTitle || "",
        author: this.draftFilters.author || "",
        tlps: Array.isArray(this.draftFilters.tlps) ? [...this.draftFilters.tlps] : [],
        minIndicators: this.normalizeNullableNumber(this.draftFilters.minIndicators),
        maxIndicators: this.normalizeNullableNumber(this.draftFilters.maxIndicators),
      };
      this.filterMenu = false;
      this.loadFeed();
    },

    applyDateSettings() {
      this.dateSettings = {
        dateField: this.draftDateSettings.dateField,
        dateFrom: this.draftDateSettings.dateFrom || "",
        dateTo: this.draftDateSettings.dateTo || "",
      };
      this.settingsMenu = false;
      this.loadFeed();
    },

    onDateRangeUpdate(range) {
      const normalizedRange = Array.isArray(range) ? range : [];
      this.draftDateSettings.dateFrom = normalizedRange[0] || "";
      this.draftDateSettings.dateTo = normalizedRange[1] || normalizedRange[0] || "";
    },

    getSelectedRangeSummary() {
      const from = this.dateSettings.dateFrom;
      const to = this.dateSettings.dateTo || this.dateSettings.dateFrom;
      if (!from && !to) {
        return "All dates";
      }
      if (from && to) {
        return `${from} ~ ${to}`;
      }
      return from || to;
    },

    sortItems(items, sortBy, sortOrder) {
      const reverse = sortOrder !== "asc";
      return items.sort((a, b) => {
        const aKey = this.getSortValue(a, sortBy);
        const bKey = this.getSortValue(b, sortBy);

        if (aKey < bKey) return reverse ? 1 : -1;
        if (aKey > bKey) return reverse ? -1 : 1;

        const aSecondary = this.getSortValue(a, "modified");
        const bSecondary = this.getSortValue(b, "modified");
        if (aSecondary < bSecondary) return reverse ? 1 : -1;
        if (aSecondary > bSecondary) return reverse ? -1 : 1;
        return 0;
      });
    },

    getSortValue(item, sortBy) {
      if (sortBy === "created") {
        return this.getDateSortValue(item.created);
      }
      if (sortBy === "modified") {
        return this.getDateSortValue(item.current_modified || item.modified || item.created);
      }
      if (sortBy === "author") {
        return String(item.author || "").toLowerCase();
      }
      if (sortBy === "name") {
        return String(item.name || "").toLowerCase();
      }
      if (sortBy === "tlp") {
        return this.getTlpSortValue(item.tlp);
      }
      if (sortBy === "indicator_count") {
        return this.getIndicatorCount(item) || 0;
      }
      return this.getDateSortValue(item.current_modified || item.modified || item.created);
    },

    getDateSortValue(value) {
      if (!value) {
        return 0;
      }
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    },

    getTlpSortValue(value) {
      const order = {
        red: 4,
        amber: 3,
        green: 2,
        white: 1,
      };
      const normalized = String(value || "").trim().toLowerCase();
      return order[normalized] || 0;
    },

    getIndicatorCount(item) {
      if (typeof item.indicator_count === "number") {
        return item.indicator_count;
      }
      if (Array.isArray(item.indicators)) {
        return item.indicators.length;
      }
      return null;
    },

    getItemDateValue(item, dateField) {
      if (dateField === "created") {
        return item.created;
      }
      return item.current_modified || item.modified || item.created;
    },

    getPulseUrl(item) {
      const pulseId = item && item.id ? String(item.id).trim() : "";
      return pulseId ? `https://otx.alienvault.com/pulse/${pulseId}` : "#";
    },

    normalizeTlp(value) {
      if (!value) return "N/A";
      return String(value).toUpperCase();
    },

    formatDate(value) {
      if (!value) return "-";
      try {
        return new Date(value).toLocaleString();
      } catch (e) {
        return value;
      }
    },

    normalizeNullableNumber(value) {
      if (value === "" || value === null || typeof value === "undefined") {
        return null;
      }

      const trimmed = String(value).trim();
      if (trimmed === "") {
        return null;
      }

      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    },

    isFiniteNumber(value) {
      if (typeof value === "boolean" || value === null || typeof value === "undefined") {
        return false;
      }

      const trimmed = String(value).trim();
      return trimmed !== "" && Number.isFinite(Number(trimmed));
    },

    toIsoStartOfDay(dateValue) {
      return new Date(`${dateValue}T00:00:00`).toISOString();
    },

    toIsoEndOfDay(dateValue) {
      return new Date(`${dateValue}T23:59:59.999`).toISOString();
    },

    resetFilterSettings() {
      this.draftFilters = {
        softwareTitle: "",
        author: "",
        tlps: [],
        minIndicators: "",
        maxIndicators: "",
      };

      this.filters = {
        softwareTitle: "",
        author: "",
        tlps: [],
        minIndicators: null,
        maxIndicators: null,
      };

      this.filterMenu = false;
      this.loadFeed();
    },
  },
};
</script>

<style scoped>
.otx-feed-card {
  height: 100%;
  min-height: 640px;
  display: flex;
  flex-direction: column;
  background: transparent;
}

.feed-title-wrap {
  min-width: 0;
  display: flex;
  align-items: center;
  min-height: 32px;
}

.header-row {
  min-height: 32px;
  margin-top: -12px;
}

.otx-toolbar-controls {
  flex-shrink: 0;
  min-height: 32px;
  margin-top: 10px;
}

.otx-icon-btn {
  align-self: center;
}

.meta-row {
  min-height: 20px;
}

.active-filter-summary {
  min-width: 0;
}

.otx-updated-text {
  flex-shrink: 0;
}

.otx-content-area {
  position: relative;
  flex: 1 1 auto;
  min-height: 520px;
}

.otx-scroll-container {
  flex: 1 1 auto;
  min-height: 520px;
  max-height: 520px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.empty-state {
  min-height: 520px;
  display: flex;
  align-items: center;
}

.otx-list-fill {
  min-height: 100%;
}

.otx-list-item {
  align-items: flex-start;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.otx-list-item:last-child {
  border-bottom: none;
}

.otx-title {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: break-word;
  line-height: 1.35;
}

.otx-title-link {
  color: inherit;
  text-decoration: none;
  word-break: break-word;
}

.otx-title-link:hover,
.otx-title-link:focus {
  text-decoration: underline;
}

.otx-subtitle {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: break-word;
  line-height: 1.25;
}

.otx-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.92);
  z-index: 2;
  display: flex;
  align-items: stretch;
}

.otx-skeleton-fill {
  width: 100%;
  height: 100%;
}

.sort-select-row {
  min-height: 40px;
}

.otx-action-btn {
  border-color: #020446 !important;
  color: #020446 !important;
  text-transform: none;
  letter-spacing: 0;
}
</style>